import {
  createElement,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import "./GhostType.css";

const jitter = (ms) => ms * (0.72 + Math.random() * 0.56);

function splitStream(completion, streamBy) {
  if (streamBy === "char") return Array.from(completion);
  return completion.match(/\S+\s*|\s+/g) ?? [completion];
}

const GhostType = forwardRef(function GhostType(
  {
    text = "Build anything",
    completions = [" with a single prompt."],
    as = "h2",
    textColor,
    accentColor = "#8B5CF6",
    ghostOpacity = 0.38,
    align = "left",
    typeSpeed = 45,
    streamSpeed = 70,
    streamBy = "word",
    thinkDelay = 600,
    acceptDelay = 1200,
    holdDelay = 1800,
    clearStyle = "select",
    caret = "bar",
    caretBlink = true,
    hint = true,
    hintLabel = "Tab",
    sweep = true,
    loop = true,
    startOnView = true,
    pauseOnHover = true,
    clickToAccept = true,
    paused = false,
    instant = false,
    onAccept,
    onComplete,
    className = "",
    style,
  },
  ref
) {
  const rootRef = useRef(null);
  const timersRef = useRef([]);
  const pausedRef = useRef(paused);
  const hoverRef = useRef(false);
  const compIdxRef = useRef(0);

  const [started, setStarted] = useState(!startOnView);
  const [baseLen, setBaseLen] = useState(0);
  const [ghost, setGhost] = useState("");
  const [accepted, setAccepted] = useState("");
  const [compIdx, setCompIdx] = useState(0);
  const [phase, setPhase] = useState("typing");
  const [sweepOn, setSweepOn] = useState(false);
  const [showCaret, setShowCaret] = useState(true);
  const [done, setDone] = useState(false);

  compIdxRef.current = compIdx;

  const reducedMotion = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((id) => window.clearTimeout(id));
    timersRef.current = [];
  }, []);

  const schedule = useCallback((fn, ms) => {
    const id = window.setTimeout(() => {
      if (pausedRef.current || hoverRef.current) {
        schedule(fn, 120);
        return;
      }
      fn();
    }, jitter(ms));
    timersRef.current.push(id);
  }, []);

  const afterClear = useCallback(() => {
    const next = compIdxRef.current + 1;
    if (next >= completions.length) {
      if (loop) {
        setCompIdx(0);
        setPhase("think");
      } else {
        setDone(true);
        setPhase("idle");
        setShowCaret(false);
      }
    } else {
      setCompIdx(next);
      setPhase("think");
    }
  }, [completions.length, loop]);

  const acceptNow = useCallback(() => {
    const completion = completions[compIdxRef.current] ?? "";
    if (!completion) return;
    clearTimers();
    setGhost("");
    setAccepted(completion);
    setPhase("hold");
    setSweepOn(sweep);
    onAccept?.(completion, compIdxRef.current);

    if (sweep) schedule(() => setSweepOn(false), 520);

    schedule(() => {
      const isLast = compIdxRef.current >= completions.length - 1;
      if (!loop && isLast) {
        setPhase("idle");
        setDone(true);
        setShowCaret(false);
        onComplete?.();
        return;
      }
      setPhase("clear");
      if (clearStyle === "backspace") {
        let i = completion.length;
        const step = () => {
          i -= 1;
          setAccepted(completion.slice(0, i));
          if (i > 0) schedule(step, 22);
          else {
            setAccepted("");
            afterClear();
          }
        };
        step();
      } else {
        setAccepted("");
        afterClear();
      }
    }, holdDelay);
  }, [afterClear, clearStyle, clearTimers, completions, holdDelay, loop, onAccept, onComplete, schedule, sweep]);

  const restart = useCallback(() => {
    clearTimers();
    setBaseLen(0);
    setGhost("");
    setAccepted("");
    setCompIdx(0);
    setPhase("typing");
    setSweepOn(false);
    setDone(false);
    setShowCaret(true);
    if (!started) setStarted(true);
  }, [clearTimers, started]);

  const skipToNext = useCallback(() => {
    clearTimers();
    setGhost("");
    setAccepted("");
    const next = compIdxRef.current + 1;
    if (next >= completions.length) {
      if (loop) setCompIdx(0);
      else {
        setDone(true);
        setPhase("idle");
        setShowCaret(false);
      }
    } else setCompIdx(next);
    setPhase("think");
  }, [clearTimers, completions.length, loop]);

  useImperativeHandle(ref, () => ({
    accept: acceptNow,
    next: skipToNext,
    restart,
  }));

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);

  useEffect(() => {
    if (!instant) return undefined;
    clearTimers();
    const completion = completions[0] ?? "";
    setBaseLen(text.length);
    setGhost("");
    setAccepted(completion);
    setShowCaret(false);
    setDone(true);
    setPhase("idle");
    onComplete?.();
    return undefined;
  }, [instant, text, completions, clearTimers, onComplete]);

  useEffect(() => {
    if (!startOnView || started) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          io.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [startOnView, started]);

  useEffect(() => {
    if (instant) return undefined;
    if (!started || paused || done) return undefined;
    if (reducedMotion) {
      const completion = completions[0] ?? "";
      setBaseLen(text.length);
      setAccepted(completion);
      setShowCaret(false);
      setDone(true);
      setPhase("idle");
      onComplete?.();
      return undefined;
    }

    if (phase === "typing") {
      if (baseLen >= text.length) {
        if (completions.length === 0) {
          setShowCaret(false);
          setDone(true);
          setPhase("idle");
          onComplete?.();
        } else {
          setPhase("think");
        }
        return undefined;
      }
      schedule(() => setBaseLen((n) => n + 1), typeSpeed);
      return undefined;
    }

    if (phase === "think") {
      schedule(() => {
        setGhost("");
        setPhase("stream");
      }, thinkDelay);
      return undefined;
    }

    if (phase === "stream") {
      const completion = completions[compIdx] ?? "";
      const parts = splitStream(completion, streamBy);
      const stepMs = streamBy === "char" ? streamSpeed / 3 : streamSpeed;
      let idx = 0;
      let cancelled = false;
      const tick = () => {
        if (cancelled) return;
        idx += 1;
        setGhost(parts.slice(0, idx).join(""));
        if (idx < parts.length) schedule(tick, stepMs);
        else {
          setPhase("wait");
          schedule(acceptNow, acceptDelay);
        }
      };
      tick();
      return () => {
        cancelled = true;
      };
    }

    return undefined;
  }, [
    instant,
    started,
    paused,
    done,
    reducedMotion,
    phase,
    baseLen,
    text,
    completions,
    compIdx,
    typeSpeed,
    thinkDelay,
    streamSpeed,
    streamBy,
    acceptDelay,
    acceptNow,
    onComplete,
    schedule,
    clearTimers,
  ]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Tab" || phase !== "wait") return;
      e.preventDefault();
      acceptNow();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phase, acceptNow]);

  const onClick = () => {
    if (!clickToAccept) return;
    if (phase === "wait" || phase === "stream") acceptNow();
    else if (phase === "hold") skipToNext();
  };

  const mergedStyle = {
    ...(style || {}),
    ...(textColor ? { color: textColor } : {}),
    ["--ghost-accent"]: accentColor,
    ["--ghost-opacity"]: ghostOpacity,
  };

  const base = reducedMotion ? text : text.slice(0, baseLen);
  const srText = `${text}${completions[0] ?? ""}`;

  const caretClass = [
    "ghost-type__caret",
    caret === "block" && "ghost-type__caret--block",
    caret === "underscore" && "ghost-type__caret--underscore",
    caretBlink && showCaret && "is-blink",
  ]
    .filter(Boolean)
    .join(" ");

  return createElement(
    as,
    {
      ref: rootRef,
      className: `ghost-type ghost-type--align-${align} ${className}`.trim(),
      style: mergedStyle,
      onClick,
      onMouseEnter: () => {
        if (pauseOnHover) hoverRef.current = true;
      },
      onMouseLeave: () => {
        hoverRef.current = false;
      },
      "aria-label": srText,
    },
    createElement(
      "span",
      { className: "ghost-type__inner", "aria-hidden": true },
      base,
      accepted
        ? createElement(
            "span",
            { className: `ghost-type__accepted${sweepOn ? " is-sweep" : ""}` },
            accepted
          )
        : null,
      ghost ? createElement("span", { className: "ghost-type__ghost" }, ghost) : null,
      showCaret && !done ? createElement("span", { className: caretClass }) : null,
      hint && (phase === "wait" || phase === "stream")
        ? createElement(
            "span",
            { className: "ghost-type__hint" },
            createElement("kbd", null, hintLabel)
          )
        : null
    )
  );
});

export default GhostType;
